import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'react-loader';
import { Person } from '@enview/interface/types/Person';
import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import Filters from '../PageElements/Filters';
import { OptionType } from '../DropDowns/MultipleSelectDropDown';
import './ActivityFeed.scss';
import { ActivityFeedSection, Section } from './ActivityFeedSection';
import CommentForm from './CommentForm/CommentForm';
import { Activity } from './Activity';
import { State } from '../../../dux/@types';
import { trackFilterActivityFeed } from '../../../analytics/ActivityFeedAnalytics';
import { filterActivities } from './ActivityFeedHelper';
import { SortOrder, SortBy } from '../../../models/SortOrder';
import { UserActionAPI } from '../../../api';
import { getTeamMode } from '../../../dux/TeamModeDux';

const NO_RESULTS_MESSAGE = 'No results found.';

type ActivityFeedProps = {
  activities: Activity[];
  aiSummary?: boolean;
  bill?: BillDetailed;
  billVersionDisplayed?: BillVersion;
  filterOptions: OptionType<string>[];
  person?: Person;
};

const sortActivitiesIntoSectionByDay = (activities: Activity[]): Section[] => {
  return activities.reduce((sections: Section[], activity) => {
    const dayExistsIndex = sections.findIndex((section) => {
      return section.date.isSame(activity.date, 'day');
    });
    if (dayExistsIndex > -1) {
      sections[dayExistsIndex].activities.push(activity);
    } else {
      sections.push({
        date: activity.date,
        activities: [activity],
      });
    }
    return sections;
  }, []);
};

// Activity Sort
const sortOptions = [
  new SortOrder(SortBy.RECENT_ACTIVITY, false),
  new SortOrder(SortBy.RECENT_ACTIVITY, true),
];

const ActivityFeed = (props: ActivityFeedProps): ReactElement => {
  const [sortBy, setSortBy] = useState(sortOptions[0]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const teamModeId = useSelector(getTeamMode);
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const { activities, billVersionDisplayed, filterOptions, bill, person, aiSummary } =
    props;
  const filteredActivities = filterActivities(activities, selectedFilters);
  const days = sortActivitiesIntoSectionByDay(filteredActivities);
  const [, { isLoading }] = UserActionAPI.endpoints.addUserAction.useMutation();

  const noActivityResults = (
    <div className="card">
      <div className="card-body">
        <div className="alert alert-secondary" role="alert">
          {NO_RESULTS_MESSAGE}
        </div>
      </div>
    </div>
  );

  return (
    <div className="activity-feed">
      <div className="activity-feed-header">
        <div className="comment-form-body">
          <CommentForm
            aiSummary={aiSummary}
            bill={bill}
            billVersionDisplayed={billVersionDisplayed}
            person={person}
          />
        </div>
        <div className="filters">
          <Filters
            filterOptions={filterOptions}
            onFiltersChange={(selectedFilterValues) => {
              setSelectedFilters(selectedFilterValues);
              trackFilterActivityFeed(
                selectedFilterValues,
                teamModeId,
                orgUser,
                bill,
                person,
              );
            }}
            onSortSelect={(selectedSort) => {
              setSortBy(selectedSort);
              trackFilterActivityFeed(
                [selectedSort.getQueryParam()?.toString() || ''],
                teamModeId,
                orgUser,
                bill,
                person,
              );
            }}
            selectedFilters={selectedFilters}
            selectedSort={sortBy}
            sortOptions={sortOptions}
          />
        </div>
      </div>
      <div
        style={{
          position: 'relative',
          display: isLoading ? 'flex' : 'none',
          padding: '8px',
        }}
      >
        <Loader color="#d8d8d8" loaded={!isLoading} scale={0.75} />
      </div>
      {filteredActivities.length === 0 ? (
        noActivityResults
      ) : (
        <div className="activity-feed-body">
          {days
            .sort((a, b) =>
              sortBy.isReversed ? a.date.diff(b.date) : b.date.diff(a.date),
            )
            .map((section) => (
              <ActivityFeedSection
                isReversed={sortBy.isReversed}
                key={section.date.format()}
                section={section}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
